'use client';

import { initRum, InitRumOptions, datadogRum } from '@moodys-bss/ui-datadog';
import { useEffect } from 'react';

export type UseDatadogRumOptions = Pick<
  InitRumOptions,
  'allowedTracingUrls' | 'applicationId' | 'clientToken' | 'env' | 'isSessionReplayEnabled' | 'service'
>;

/**
 * Custom hook for initializing and using Datadog Real User Monitoring (RUM).
 *
 * @param options - The options for configuring Datadog RUM.
 */
export const useDatadogRum = ({
  allowedTracingUrls,
  applicationId,
  clientToken,
  env,
  isSessionReplayEnabled,
  service,
}: UseDatadogRumOptions) => {
  const isServer = typeof window === 'undefined';
  const isLocalhost = !isServer && window.location.hostname === 'localhost';
  const allowRum = !isLocalhost && !isServer;

  // For the call to `initRum` we should have all of the required options on first render.
  // It is not expected any of them will change during the lifecycle of the component.
  useEffect(() => {
    if (allowRum) {
      initRum({
        allowedTracingUrls,
        applicationId,
        clientToken,
        env,
        service,
        version: process.env.NEXT_PUBLIC_RELEASE_VERSION ?? 'N/A',
        sessionReplaySampleRate: 100,
        isSessionReplayEnabled: true,
        trackResources: false,
        defaultPrivacyLevel: 'mask',
        enablePrivacyForActionName: true,
        startSessionReplayRecordingManually: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For Session Replay, we rely on a Split feature flag to determine if it should be enabled.
  // This takes some time to resolve, so we need to check if it is enabled after the initial render.
  useEffect(() => {
    if (allowRum && isSessionReplayEnabled) {
      datadogRum.startSessionReplayRecording();
    }
  }, [allowRum, isSessionReplayEnabled]);
};
